import React, { useState, useEffect } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { Pencil } from 'lucide-react';
import ProfileSection from '@/pages/UserManagement/UserList/components/ProfileSection';
import { Button } from '@/components/ui/button';
import {
  DEFAULT_ADD_USER_SCHEMA_VALUES,
  UserFormInputs,
  userSchema,
} from './validationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '@/components/hooks/use-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from 'fluent-components';
import { UserProfile } from '@/types';
import { useQuery } from '@apollo/client';
import { MultiSelectRole } from './MultiSelectRole';
import ConfirmationPopup from '@/components/common/ConfirmationPopup';
import { USER_ROLES } from '@/graphql/queries/user-management';
import { mixPanelServiceInstance } from '@/analytics/mixpanel';
import { mixPanelEvents } from '@/analytics/mixpanel/events';
import { ROUTE } from '@/routes';
import {
  AddUserFormProps,
  Concept,
  Include,
  RoleOption,
  RoleSet,
} from '../../UserList/types';
import useUserInfo from '@/components/Authenticate/useUserInfo';

const AddUserForm: React.FC<AddUserFormProps> = ({
  userManagerId,
  onEditToggle,
  onFormSubmit,
  userProfile,
  state,
}) => {
  const { data: userRoles } = useQuery(USER_ROLES);

  const navigate = useNavigate();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [disableButton, setDisableButton] = useState<boolean>(false);

  const { id } = useParams();
  const { userInfo } = useUserInfo();

  useEffect(() => {
    setIsEditing(!id);
  }, [id]);

  useEffect(() => {
    if (userProfile && !state && !isEditing) {
      setIsEditing(true);
    }
  }, [isEditing, state, userProfile]);

  useEffect(() => {
    if (!userRoles?.roles) return;

    const formattedRoles: RoleOption[] = userRoles.roles.flatMap(
      (roleSet: RoleSet) =>
        roleSet.compose?.include?.flatMap(
          (include: Include) =>
            include.concept?.map((concept: Concept) => ({
              value: concept.code,
              label: concept.display,
            })) || []
        ) || []
    );
    setRoleOptions(formattedRoles);
  }, [userRoles]);

  const form = useForm<UserFormInputs>({
    defaultValues: DEFAULT_ADD_USER_SCHEMA_VALUES,
    resolver: zodResolver(userSchema),
    mode: 'onChange',
  });

  const {
    formState: { errors },
    reset,
  } = form;
  useEffect(() => {
    const number = userProfile?.additionalInformation?.phone_number
      ?.split('-')
      ?.join('')
      ?.replace(/^91/, '');

    if (
      userProfile?.additionalInformation?.emr_id &&
      userProfile?.additionalInformation?.medplum_id
    ) {
      const profile: UserProfile = {
        id: userProfile?.id,
        name: `${userProfile?.firstName || ''} ${
          userProfile?.lastName || ''
        }`.trim(),
        fluentEmail: userProfile?.email || 'N/A',
        secondaryEmail:
          userProfile?.additionalInformation?.secondary_email || 'N/A',
        firstName: userProfile?.firstName || 'N/A',
        lastName: userProfile?.lastName || 'N/A',
        roles: userProfile?.additionalInformation?.permify_roles || [],
        phone_number: userProfile.additionalInformation.phone_number || '',
        status: userProfile.enabled ? 'Active' : 'Suspended',
        userId: '',
      };
      reset({
        roles: profile.roles,
        fluentEmail: profile.fluentEmail,
        personalEmail: profile?.secondaryEmail,
        phone_number: number,
        firstName: profile.firstName,
        lastName: profile.lastName,
      });

      return;
    } else {
      reset({
        roles: userProfile?.additionalInformation?.permify_roles,
        fluentEmail: userProfile.email,
        personalEmail: userProfile?.additionalInformation?.secondary_email,
        phone_number: number,
        firstName: userProfile?.firstName,
        lastName: userProfile?.lastName,
      });
      return;
    }
  }, [reset, userProfile]);

  useEffect(() => {
    if (Object.keys(errors || {}).length > 0) {
      toast({
        title: 'Error',
        description: (
          <ul>
            {Object.values(errors).map((error, idx) => (
              <li key={idx}>{error.message}</li>
            ))}
          </ul>
        ),
        variant: 'destructive',
      });
    }
  }, [errors]);

  const onSubmit = (data: UserFormInputs) => {
    mixPanelServiceInstance.track(
      mixPanelEvents.USER_EVENTS.EDIT_USER_COMPLETED,
      {
        user_manager_id: userManagerId,
        user_id: userProfile?.id,
      }
    );
    setDisableButton(true);
    onFormSubmit({
      ...data,
      roles: data.roles || [],
    });
  };

  const handleUserCancel = () => {
    mixPanelServiceInstance.track(
      mixPanelEvents.USER_EVENTS.ADD_USER_CANCELLED,
      {
        user_manager_id: userManagerId,
        user_id: userProfile?.id,
      }
    );
    navigate(ROUTE.USER_MANAGEMENT);
    toast({
      title: 'Changes discarded',
      variant: 'default',
    });
  };

  const toggleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const newEditState = !isEditing;
    setIsEditing(newEditState);
    onEditToggle(newEditState);
  };
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-[80%] mb-14">
          <ProfileSection>
            <div className="flex py-2 justify-between items-center mb-4">
              <h2 className="text-sm text-iris-160 font-medium uppercase">
                Personal Details
              </h2>
              {userProfile && !state && !isEditing && (
                <Button
                  variant="link"
                  type="button"
                  className="text-sm text-iris-160 font-medium flex gap-2 items-center mr-[-24%]"
                  onClick={toggleEdit}
                >
                  <Pencil size={14} />
                  Edit user
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-6 mb-8">
              <FormField
                disabled={!isEditing}
                control={form.control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="font-normal">First name</FormLabel>
                    <FormControl className="focus-within:outline-none focus-within:ring-2">
                      <Input
                        placeholder="Enter First name"
                        {...field}
                        maxLength={50}
                        type="text"
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.target.value = e.target.value.replace(
                            /[^A-Za-z]/g,
                            ''
                          );
                        }}
                        className="focus:outline-none focus:ring-0"
                      />
                    </FormControl>
                    <FormMessage className="opacity-70">
                      {fieldState.error?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                disabled={!isEditing}
                control={form.control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Last name</FormLabel>
                    <FormControl className="focus-within:outline-none focus-within:ring-2">
                      <Input
                        placeholder="Enter Last name"
                        {...field}
                        maxLength={50}
                        type="text"
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.target.value = e.target.value.replace(
                            /[^A-Za-z]/g,
                            ''
                          );
                        }}
                        className="focus:outline-none focus:ring-0"
                      />
                    </FormControl>
                    <FormMessage className="opacity-70">
                      {fieldState.error?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-6 mb-8">
              <FormField
                disabled={!isEditing}
                control={form.control}
                name="phone_number"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Phone number</FormLabel>
                    <FormControl className="focus-within:outline-none focus-within:ring-2">
                      <Input
                        placeholder="Phone no."
                        {...field}
                        maxLength={10}
                        error={!!fieldState.error}
                        type="number"
                        className="focus:outline-none focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-moz-appearance]:textfield"
                      />
                    </FormControl>
                    <FormMessage className="opacity-70">
                      {fieldState.error?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                disabled={!isEditing}
                control={form.control}
                name="personalEmail"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="font-normal">
                      Personal email address
                    </FormLabel>
                    <FormControl className="focus-within:outline-none focus-within:ring-2">
                      <Input
                        placeholder="Enter email address"
                        {...field}
                        maxLength={50}
                        type="email"
                        error={!!fieldState.error}
                        className="focus:outline-none focus:ring-0"
                      />
                    </FormControl>
                    <FormMessage className="opacity-70">
                      {fieldState.error?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>
          </ProfileSection>

          <ProfileSection title="Organisational Details">
            <div className="grid grid-cols-2 gap-6">
              <FormField
                disabled={!isEditing}
                control={form.control}
                name="roles"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Roles</FormLabel>
                    <MultiSelectRole
                      disabled={!isEditing}
                      options={roleOptions}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Select Role(s)"
                      defaultValue={field.value}
                      animation={2}
                      maxCount={3}
                      className="w-full"
                    />
                    <FormMessage className="opacity-70">
                      {fieldState.error?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                disabled
                control={form.control}
                name="fluentEmail"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="font-normal">
                      Fluent email address
                    </FormLabel>
                    <FormControl className="focus-within:outline-none focus-within:ring-2">
                      <Input
                        placeholder="Enter fluent email id"
                        {...field}
                        maxLength={50}
                        type="email"
                        error={!!fieldState.error}
                        className="focus:outline-none focus:ring-0"
                      />
                    </FormControl>
                    <FormMessage className="opacity-70">
                      {fieldState.error?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>
          </ProfileSection>

          {isEditing && (
            <div className="flex justify-end fixed bottom-0 w-full bg-white p-4 gap-4 right-0 border-t-2">
              <Button
                type="button"
                className="bg-transparent text-black"
                onClick={(e) => {
                  e.preventDefault();
                  mixPanelServiceInstance.track(
                    mixPanelEvents.USER_EVENTS.EDIT_USER_CANCELLED,
                    {
                      user_manager_id: userInfo?.id || 'N/A',
                      user_id: userProfile?.id,
                    }
                  );
                  setIsDeleteDialogOpen(true);
                }}
              >
                Cancel
              </Button>

              <Button disabled={disableButton} type="submit" className="px-10">
                Save changes
              </Button>
            </div>
          )}
        </form>
      </Form>

      <ConfirmationPopup
        title="Exit without saving?"
        description="The information you have entered will not be saved. Please confirm if you want to abandon this process."
        primaryButtonConfig={{
          label: 'No',
          handleClick: () => {
            setIsDeleteDialogOpen(false);
          },
          variant: 'default',
        }}
        secondaryButtonConfig={{
          label: 'Yes',
          handleClose: (e: React.MouseEvent<HTMLButtonElement>) => {
            toggleEdit(e);
            setIsDeleteDialogOpen(false);
            handleUserCancel();
          },
        }}
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onActionComplete={() => setIsDeleteDialogOpen(false)}
      />
    </>
  );
};

export default AddUserForm;
